import { Loader } from "lucide-react";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { Button } from "./Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { LoadingWrapper } from "./LoadingWrapper";

export type ConfirmDialogOptions = {
  color?: "primary" | "destructive";
  title?: React.ReactNode;
  message?: React.ReactNode;
  confirmLabel?: string;
  closeLabel?: string;
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  alert?: boolean;
};

export type ConfirmDialogHandle = {
  openDialog: (options: ConfirmDialogOptions) => void;
};

export const ConfirmDialog = forwardRef((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<ConfirmDialogOptions & { isOpen: boolean }>({ isOpen: false });

  const openDialog = useCallback((options: ConfirmDialogOptions) => {
    setState({
      isOpen: true,
      color: options.color ?? "primary",
      title: options.title ?? "Are you sure?",
      message: options.message ?? <></>,
      confirmLabel: options.confirmLabel ?? "Confirm",
      closeLabel: options.closeLabel ?? "Close",
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      alert: options.alert ?? false,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  const handleConfirm = async () => {
    if (state.onConfirm) {
      setLoading(true);
      try {
        await state.onConfirm();
      } finally {
        setLoading(false);
      }
    }
    closeDialog();
  };

  const handleCancel = async () => {
    await state.onCancel?.();
    closeDialog();
  };

  useImperativeHandle(ref, (): ConfirmDialogHandle => {
    return {
      openDialog,
    };
  }, [openDialog]);

  return (
    <Dialog
      open={state.isOpen}
      onOpenChange={(isOpen) => {
        if (!state.alert || isOpen) {
          setState((prev) => ({ ...prev, isOpen }));
        }
      }}
    >
      <DialogContent className="p-0 sm:max-w-[425px]" showCloseButton={!state.alert}>
        <LoadingWrapper isLoading={loading} overlayClassName="rounded-xl">
          <div className="p-6">
            <DialogHeader className="pb-6">
              <DialogTitle>{state.title}</DialogTitle>
              <DialogDescription />
            </DialogHeader>
            <div>{state.message}</div>
            <DialogFooter className={state.alert ? "pt-8" : "grid grid-cols-2 gap-4 pt-8"}>
              {!state.alert && (
                <DialogClose asChild>
                  <Button disabled={loading} onClick={() => void handleCancel()}>
                    {state.closeLabel}
                  </Button>
                </DialogClose>
              )}
              <Button color={state.color} disabled={loading} onClick={() => void handleConfirm()}>
                {loading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                {state.confirmLabel}
              </Button>
            </DialogFooter>
          </div>
        </LoadingWrapper>
      </DialogContent>
    </Dialog>
  );
});
